import jsPDF from "jspdf";
import autoTable, { RowInput } from "jspdf-autotable";
import { useTranslation } from "react-i18next";

import { useVideoSurveyInventory } from "../../../../hooks/useVideoSurveyInventory";
import { RoomJob } from "../../../../models/sales";
import { handleGetConvertValue } from "../../../../util/converts";
import { getMeasurement, getWeightMeasurement } from "../../../../util/getMeasurement";

const useShareInventory = () => {
  const { t: translate } = useTranslation();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const formatVolumeUnit = (code?: string) => {
    if (!code) return "";

    return code.replace(/[³]/g, "3");
  };

  const { inventory, sumItemsAndMaterials, totalVolume, calculateValuesByRoom } =
    useVideoSurveyInventory();

  const doc = new jsPDF();

  const totalItems = (room?: RoomJob) => {
    return sumItemsAndMaterials({ room, type: "quantity" });
  };

  const renderDocument = () => {
    doc.text(JSON.stringify(inventory), 10, 10);
    doc.save("inventory.pdf");
  };

  const generatePdf = () => {
    const pdf = new jsPDF("p", "pt", "a4");

    pdf.setFontSize(18);
    pdf.setFont("Manrope", "normal", "bold");
    pdf.setTextColor("#465869");
    pdf.text(translate("inventoryList"), 235, 30);

    const columnItems = [
      { header: translate("item"), dataKey: "item" },
      { header: translate("qty"), dataKey: "qty" },
      { header: translate("volume"), dataKey: "volume" },
      { header: translate("weight"), dataKey: "weight" },
    ];

    const columnStylesProps = {
      fillColor: "#fff",
      textColor: "#465869",
      fontSize: 14,
    };

    for (const room of inventory) {
      autoTable(pdf, {
        columnStyles: {
          headerName: {
            ...columnStylesProps,
            fontSize: 18,
            fontStyle: "bold",
            valign: "middle",
            cellWidth: 200,
          },
          headerQty: {
            ...columnStylesProps,
            fontSize: 12,
            valign: "middle",
            halign: "right",
          },
          headerVolume: {
            ...columnStylesProps,
            fontSize: 12,
            valign: "middle",
            halign: "right",
          },
          headerWeight: {
            ...columnStylesProps,
            fontSize: 12,
            valign: "middle",
            halign: "right",
          },
        },
        headStyles: {
          fillColor: "#fff",
          textColor: "#FFF",
          fontSize: 0,
          valign: "middle",
        },
        body: [
          {
            headerName: room.name,
            headerQty: `${totalItems(room)} ${
              totalItems(room) > 0 ? translate("items") : translate("item")
            }`,
            headerVolume: `${totalVolume(room)} ${formatVolumeUnit(
              measurement?.code?.toUpperCase()
            )}`,
            headerWeight: `${(Number(totalVolume(room)) * 1000).toFixed(
              2
            )} ${measurementWeight?.code?.toUpperCase()}`,
          },
        ],
        columns: [
          { header: "headerName", dataKey: "headerName" },
          { header: "headerQty", dataKey: "headerQty" },
          { header: "headerVolume", dataKey: "headerVolume" },
          { header: "headerWeight", dataKey: "headerWeight" },
        ],
      });

      const rowItems = [];

      if (room?.itemsJob && room?.itemsJob.length > 0) {
        for (const item of room.itemsJob) {
          const newRowValues = {
            item: item?.itemJob?.description,
            qty: item?.itemJob?.quantity,
            volume: `${handleGetConvertValue(
              Number(item?.itemJob?.volume),
              measurement?.code,
              measurement?.decimalPlaces
            )} ${formatVolumeUnit(measurement?.code)}`,
            weight: `${(Number(item?.itemJob?.volume || "0") * 1000).toFixed(
              2
            )} ${measurementWeight?.code?.toUpperCase()}`,
          };

          rowItems.push(newRowValues);
        }
      }

      autoTable(pdf, {
        columnStyles: {
          item: { ...columnStylesProps, cellWidth: 180 },
          qty: { ...columnStylesProps, cellWidth: 120 },
          volume: { ...columnStylesProps, cellWidth: 120 },
          weight: { ...columnStylesProps, cellWidth: 120 },
        },
        headStyles: {
          fillColor: "#fff",
          textColor: "#9CA6AF",
          fontSize: 10,
        },
        body: rowItems as RowInput[],
        columns: columnItems,
      });

      const rowMaterials = [];

      if (room?.materials && room?.materials.length > 0) {
        autoTable(pdf, {
          columnStyles: {
            0: {
              ...columnStylesProps,
              fontSize: 12,
              fontStyle: "bold",
              valign: "middle",
            },
          },
          body: [["Materials"]],
        });

        for (const material of room.materials) {
          const newRowValues = [
            material?.serviceBooking?.name,
            Number(material.quantity),
            `${handleGetConvertValue(
              Number(material?.volume),
              measurement?.code,
              measurement?.decimalPlaces
            )} ${formatVolumeUnit(measurement?.code)}`,

            `${(Number(material?.volume || "0") * 1000).toFixed(
              2
            )} ${measurementWeight?.code?.toUpperCase()}`,
          ];

          rowMaterials.push(newRowValues);
        }
      }

      autoTable(pdf, {
        columnStyles: {
          0: { ...columnStylesProps, cellWidth: 180 },
          1: { ...columnStylesProps, cellWidth: 120 },
          2: { ...columnStylesProps, cellWidth: 120 },
          3: { ...columnStylesProps, cellWidth: 120 },
        },
        body: rowMaterials,
      });
    }

    autoTable(pdf, {
      columnStyles: {
        total: {
          ...columnStylesProps,
          fontSize: 14,
          fontStyle: "bold",
          valign: "middle",
          cellWidth: 140,
        },
        footerQty: {
          ...columnStylesProps,
          fontSize: 14,
          textColor: "#93A9BD",
          valign: "middle",
          halign: "right",
          cellWidth: 35,
        },
        footerQtyValue: {
          ...columnStylesProps,
          fontSize: 14,
          fontStyle: "bold",
          valign: "middle",
          halign: "left",
          cellWidth: 70,
        },
        footerVolume: {
          ...columnStylesProps,
          fontSize: 14,
          textColor: "#93A9BD",
          valign: "middle",
          halign: "right",
          cellWidth: 55,
        },
        footerVolumeValue: {
          ...columnStylesProps,
          fontSize: 14,
          fontStyle: "bold",
          valign: "middle",
          halign: "left",
          cellWidth: 100,
        },
        footerWeight: {
          ...columnStylesProps,
          fontSize: 14,
          textColor: "#93A9BD",
          valign: "middle",
          halign: "right",
          cellWidth: 55,
        },
        footerWeightValue: {
          ...columnStylesProps,
          fontSize: 14,
          fontStyle: "bold",
          valign: "middle",
          halign: "left",
          cellWidth: 100,
        },
      },
      headStyles: {
        fillColor: "#fff",
        textColor: "#FFF",
        fontSize: 0,
        valign: "middle",
      },
      body: [
        {
          total: translate("total"),
          footerQty: translate("qty"),
          footerQtyValue: `${calculateValuesByRoom().quantity || 0}`,
          footerVolume: translate("volume"),
          footerVolumeValue: `${calculateValuesByRoom().volume || 0} ${
            formatVolumeUnit(measurement?.code) || ""
          }`,
          footerWeight: translate("weight"),
          footerWeightValue: `${
            calculateValuesByRoom().weight || 0
          } ${measurementWeight?.code?.toUpperCase()}`,
        },
      ],
      columns: [
        { header: "total", dataKey: "total" },
        { header: "footerQty", dataKey: "footerQty" },
        { header: "footerQtyValue", dataKey: "footerQtyValue" },
        { header: "footerVolume", dataKey: "footerVolume" },
        { header: "footerVolumeValue", dataKey: "footerVolumeValue" },
        { header: "footerWeight", dataKey: "footerWeight" },
        { header: "footerWeightValue", dataKey: "footerWeightValue" },
      ],
    });

    return pdf.output();
  };

  return { generatePdf, renderDocument };
};

export default useShareInventory;
